import type { ReactNode } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';

/**
 * What lifts the label onto a chip's centre line.
 *
 * `body.sm` is 14px at a 1.5 ratio, so a 21px line box; a `ScheduleChip` is 32px. Half the
 * difference is where the two read as one row. The text default of `0.5` is right for a value that
 * is also text, whose first line starts at the top of the box.
 */
const CONTROL_LABEL_OFFSET = '5.5px';

interface ReviewFactProps {
	label: string;
	/**
	 * The value leads with a control rather than text — a chip, a button — which is taller than the
	 * label beside it and centres its own text inside that height.
	 */
	alignToControl?: boolean;
	/**
	 * Size this label to its text when it sits beside another fact rather than in the shared column.
	 * Only from `md`: below it the facts stack, and a stacked pair is back in the column with
	 * everything else.
	 */
	fitLabel?: boolean;
	children: ReactNode;
}

/**
 * One labelled fact on a proposal card, laid out as a fixed label column beside its value.
 *
 * The label column is a fixed width so the values line up down the card. A reader comparing three
 * facts is reading the values, and a ragged left edge makes that harder than it needs to be.
 */
const ReviewFact = (props: ReviewFactProps) => (
	<HStack align="flex-start" gap={3} minW={0}>
		{/* Not `align="center"` on the row: a fact can carry a second line under its control — the
		    estimated-date note does — and centring the row would drop the label into the middle of the
		    block instead of leaving it against the control it names. */}
		<Text
			textStyle="body.sm"
			color="fg.muted"
			w={props.fitLabel ? { base: '72px', md: 'auto' } : '72px'}
			flexShrink={0}
			pt={props.alignToControl ? CONTROL_LABEL_OFFSET : 0.5}
		>
			{props.label}
		</Text>
		<Box minW={0} flex="1">
			{props.children}
		</Box>
	</HStack>
);

export default ReviewFact;
